import { api } from '../../../lib/api';
import type { PagedResult } from '../../administration/users/userTypes';
import type {
  CurrentRequester,
  RequestableItemType,
  RequestableItem,
  RequisitionSlipDetail,
  RequisitionSlipListItem,
  RequisitionSlipRequest,
} from './requisitionSlipTypes';

/** What the register may be narrowed by. */
export interface RequisitionSlipListQuery {
  search?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
  /** The ROW's own live-or-deleted state — the shell's key. */
  status?: string;
  /** The lifecycle. Named `stage` because `status` is the shell's. */
  stage?: string;
}

export function toQueryString(query: RequisitionSlipListQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);

  /*
    Each facet is sent whenever the key is PRESENT, empty string included: an empty value is
    the reader saying "none of these", which matches no row. A truthiness check would drop it
    and the server would read the axis as unmentioned — the three-state facet rule.
  */
  if (query.status !== undefined) params.set('status', query.status);
  if (query.stage !== undefined) params.set('stage', query.stage);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

const BASE = '/api/self-service/requisition-slips';

/**
 * The requester's own slips.
 *
 * **THERE IS NO SCOPE PARAMETER, and that is not an omission.** Which slips come back is fixed
 * by the ROUTE server-side, so nothing this client sends can widen it.
 *
 * **Nothing here approves or rejects.** Those are the approval engine's endpoints behind
 * `approvals.decide`, reaching this module through its own handler — which is what makes it
 * impossible to mark a slip approved without an approval.
 */
export const requisitionSlipsApi = {
  list: (query: RequisitionSlipListQuery, signal?: AbortSignal) =>
    api<PagedResult<RequisitionSlipListItem>>(`${BASE}${toQueryString(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) =>
    api<RequisitionSlipDetail>(`${BASE}/${id}`, { signal }),

  /** The signed-in account's own employee identity. Fails soft — read `error`. */
  me: (signal?: AbortSignal) => api<CurrentRequester>(`${BASE}/me`, { signal }),

  /** The catalog a line may name, zero-stock rows included. Carries `create`. */
  requestableItems: (signal?: AbortSignal) =>
    api<RequestableItem[]>(`${BASE}/requestable-items`, { signal }),

  /** The item types a line may ask for without naming an item. Carries `create`. */
  requestableItemTypes: (signal?: AbortSignal) =>
    api<RequestableItemType[]>(`${BASE}/requestable-item-types`, { signal }),

  create: (body: RequisitionSlipRequest) =>
    api<RequisitionSlipDetail>(BASE, { method: 'POST', body }),

  update: (id: string, body: RequisitionSlipRequest) =>
    api<RequisitionSlipDetail>(`${BASE}/${id}`, { method: 'PUT', body }),

  submit: (id: string) =>
    api<RequisitionSlipDetail>(`${BASE}/${id}/submit`, { method: 'POST' }),

  cancel: (id: string, reason: string) =>
    api<RequisitionSlipDetail>(`${BASE}/${id}/cancel`, { method: 'POST', body: { reason } }),

  remove: (id: string) => api<void>(`${BASE}/${id}`, { method: 'DELETE' }),

  restore: (id: string) => api<void>(`${BASE}/${id}/restore`, { method: 'POST' }),
};
